import { RefObject, useCallback, useEffect, useRef } from "react";
import {
  DragObjectWithType,
  DragSourceHookSpec,
  useDrag as useDragInternal,
  useDrop as useDropInternal,
} from "react-dnd";

// additional configuration necessary for react-dnd now that we use the touch
// backend
const useDragShadow = (ref: RefObject<HTMLElement>) => {
  const cloneRef = useRef<HTMLElement | null>(null);

  const styleClone = (clone: HTMLElement) => {
    clone.style.display = "block";
    clone.style.position = "absolute";
    clone.style.top = "-1000px";
    clone.style.left = "-1000px";
    clone.style.pointerEvents = "none";
    clone.style.opacity = "0.5";
    // needs to be higher than Modal
    clone.style.zIndex = "10000";
    clone.style.boxShadow = "rgba(0, 0, 0, 0.3) 0px 0px 30px";

    // this is a hack to make the token the same size as it was
    // in the gap; important when we're dragging somethign
    // with a relative size (width: 100%, etc.)
    let origWidth = ref.current?.clientWidth;
    let origHeight = ref.current?.clientHeight;
    if (origWidth && origHeight) {
      const computedStyle = getComputedStyle(ref.current!);
      origHeight -=
        parseFloat(computedStyle.paddingTop) +
        parseFloat(computedStyle.paddingBottom);
      origWidth -=
        parseFloat(computedStyle.paddingLeft) +
        parseFloat(computedStyle.paddingRight);
      clone.style.width = `${origWidth}px`;
      clone.style.height = `${origHeight}px`;
    }
  };

  useEffect(() => {
    if (!ref.current) {
      return;
    }
    const draggable = ref.current;

    const clone = draggable.cloneNode(true) as HTMLElement;

    styleClone(clone);

    document.body.appendChild(clone);
    cloneRef.current = clone;

    // we need to watch the dom for changes to the draggable element so we can
    // update the clone
    let observer: MutationObserver | null = null;
    if (MutationObserver) {
      observer = new MutationObserver(() => {
        if (cloneRef.current && ref.current) {
          const newClone = ref.current.cloneNode(true) as HTMLElement;
          cloneRef.current.replaceWith(newClone);
          styleClone(newClone);
          cloneRef.current = newClone;
        }
      });

      observer.observe(draggable, {
        attributes: true,
        childList: true,
        subtree: true,
      });
    }

    return () => {
      if (cloneRef.current) {
        document.body.removeChild(cloneRef.current);
      }
      observer?.disconnect();
    };
  }, [ref]);

  return cloneRef.current;
};

// determine if an element is scrollable
const isScrollable = (node: Element) => {
  if (!(node instanceof HTMLElement || node instanceof SVGElement)) {
    return false;
  }
  const style = getComputedStyle(node);
  return ["overflow", "overflow-x", "overflow-y"].some((propertyName) => {
    const value = style.getPropertyValue(propertyName);
    if (value === "auto" || value === "scroll") {
      // make sure the scrollable element actually has a scrollbar
      return node.scrollHeight !== node.clientHeight;
    }
    return false;
  });
};

// find the first scrollable parent of a target
const getScrollParent = (node: Element): Element => {
  let currentParent = node.parentElement;
  while (currentParent) {
    if (isScrollable(currentParent)) {
      return currentParent;
    }
    currentParent = currentParent.parentElement;
  }
  return document.scrollingElement || document.documentElement;
};

export const useDrag = <Item extends DragObjectWithType, Collect>(
  ref: RefObject<HTMLElement>,
  dndProps: DragSourceHookSpec<Item, unknown, Collect>,
) => {
  const dragInstance = useDragShadow(ref);

  const [{ isDragging }, drag] = useDragInternal({
    ...dndProps,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
      ...dndProps.collect?.(monitor),
    }),
  });

  // bind our ref to react-dnd's
  useEffect(() => {
    drag(ref.current);
  }, [drag, ref]);

  const dragAnimationState = useRef<{
    ref: number;
    timestamp: number | undefined;
  } | null>(null);
  // given a target element, find its closest scrollable parent and
  // scroll in the respective direction if we are near its edge
  const scrollOnDrag = useCallback(
    (
      target: Element | undefined,
      mouseY: number,
      timeSinceLastScroll: number,
    ) => {
      if (!target) return;

      const scrollParent = getScrollParent(target);
      const scrollParentRect = scrollParent.getBoundingClientRect();

      // how close (px) to the edge before we scroll
      // todo: make this smaller on extremely small viewports?
      const scrollThreshold = 50;

      // how fast to scroll. timeSinceLastScroll is likely either 0 (new event)
      // or [1000 / refreshRate] (~8 or ~16)
      const scrollStrength = 0.6 * timeSinceLastScroll;

      const distanceFromTop = mouseY - scrollParentRect.top;
      const distanceFromBottom = scrollParentRect.bottom - mouseY;

      if (distanceFromTop < scrollThreshold) {
        scrollParent.scrollTop -= scrollStrength;
      }
      if (distanceFromBottom < scrollThreshold) {
        scrollParent.scrollTop += scrollStrength;
      }

      // keep going until we move the mouse again or the drag ends
      const animationHandle = requestAnimationFrame((hiResTime) => {
        scrollOnDrag(
          target,
          mouseY,
          hiResTime - (dragAnimationState.current?.timestamp ?? hiResTime),
        );

        dragAnimationState.current = {
          ref: animationHandle,
          ...dragAnimationState.current,
          timestamp: hiResTime,
        };
      });

      dragAnimationState.current = {
        timestamp: undefined,
        ...dragAnimationState.current,
        ref: animationHandle,
      };
    },
    [],
  );

  useEffect(() => {
    if (!ref.current) {
      return;
    }
    const draggable = ref.current;
    const disableHighlight = (e: Event) => {
      e.preventDefault();
    };

    // I have no idea why it's `globalThis.TouchEvent`, but addEventListener
    // yelled at me otherwise
    const onMove = (e: MouseEvent | globalThis.TouchEvent) => {
      let x: number, y: number;
      // the sencond leg of both of the && are just to make typescript happy.
      // the e.type check is realistically enough
      if (e.type === "touchmove" && "touches" in e) {
        const touch = e.touches[0] || e.changedTouches[0];
        x = touch.pageX;
        y = touch.pageY;
      } else if (e.type === "mousemove" && "pageX" in e) {
        x = e.pageX;
        y = e.pageY;
      } else {
        console.error("unknown pointer event type", e.type);
        return;
      }

      if (!dragInstance) return;

      const timeSinceLastScroll = dragAnimationState.current?.timestamp
        ? performance.now() - dragAnimationState.current.timestamp
        : 0;

      // cancel the last scrolling loop, since we're about to create a new
      // one if it's necessary
      if (dragAnimationState.current) {
        cancelAnimationFrame(dragAnimationState.current.ref);
        dragAnimationState.current = null;
      }

      if (isDragging) {
        const halfW = dragInstance.offsetWidth / 2;
        const halfH = dragInstance.offsetHeight / 2;
        dragInstance.style.top = y - halfH + "px";
        dragInstance.style.left = x - halfW + "px";

        if (e.target instanceof Element) {
          scrollOnDrag(e.target, y, timeSinceLastScroll);
        }
      } else {
        dragInstance.style.top = "-1000px";
        dragInstance.style.left = "-1000px";
      }
    };

    // dismiss the shadow when you touch/mouseup
    if (!isDragging && dragInstance) {
      dragInstance.style.top = "-1000px";
      dragInstance.style.left = "-1000px";
    }

    // disable text selection
    draggable.addEventListener("mousedown", disableHighlight);

    // add drag preview, which is missing from react-dnd's touch interface apparently
    if (isDragging) {
      window.addEventListener("mousemove", onMove);
      window.addEventListener("touchmove", onMove);
    }

    return () => {
      draggable.removeEventListener("mousedown", disableHighlight);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);

      if (dragAnimationState.current) {
        cancelAnimationFrame(dragAnimationState.current.ref);
        dragAnimationState.current = null;
      }
    };
  }, [ref, dragInstance, isDragging, scrollOnDrag]);
};

// nothing special necessary yet
export const useDrop = useDropInternal;
